// Client-side mirror of API-Server's `isValidEzyvetBaseUrl`/`normalizeEzyvetBaseUrl`
// (app/control/bridgeRoutes.ts). Deliberately the same rule, not a looser or stricter one: a value
// this validator accepts but the server rejects would make the clinic's address un-savable with no
// client-side warning, and a value the server accepts but this validator rejects would block a
// legitimate address from ever being typed in. Both components that collect this address
// (`bridgedownload.tsx`'s pre-download field and `EzyvetConnection.tsx`'s settings-card field) use
// this single copy so they can't drift from each other the way the two had to be kept in sync by
// hand on the happy-bridge side (see that repo's login.js/index.js selector-agreement comments).
//
// Built on the real `URL` constructor, not hand-rolled string splitting (PR review finding, round
// 2): this value is later used by the bridge installer to make real outbound requests to a
// clinic-supplied origin, so "the host this validator approves" and "the host an actual HTTP client
// would resolve the same string to" MUST be the same computation — anything else is a parsing-
// divergence bug by construction, whether or not a concrete bypass string is known today. `new
// URL()` runs the browser's/Node's own WHATWG parsing algorithm, the same one an actual request
// would use, so there is nothing left for this function to get subtly wrong that the real request
// wouldn't also get "wrong" the same way. API-Server's copy of this function was updated to match,
// for the same single-source-of-truth reason as above — both now run the identical algorithm (the
// WHATWG URL spec) rather than two independently hand-maintained strings-pliced-apart versions that
// could drift from EACH OTHER as well as from reality.

/** Trimmed, with exactly one trailing `/` — the form every ezyVet URL concatenation assumes. */
export function normalizeEzyvetBaseUrl(url: string): string {
    const trimmed = url.trim();
    if (!trimmed) return "";
    return `${trimmed.replace(/\/+$/, "")}/`;
}

/**
 * `https://`, no userinfo (`user:pass@host`), and a host that is exactly `ezyvet.com` or ends in
 * `.ezyvet.com` — a genuine suffix-boundary check, not `.includes(...)`. Anything that isn't even a
 * parseable absolute URL (missing scheme, stray whitespace the parser can't recover from, etc.)
 * fails closed via the `catch`, the same as every other "unparseable input" case in this codebase's
 * URL-adjacent validators (e.g. happy-bridge's `hostnameFromUrl`).
 */
export function isValidEzyvetBaseUrl(url: string): boolean {
    let parsed: URL;
    try {
        parsed = new URL(url);
    } catch {
        return false;
    }
    if (parsed.protocol !== "https:") return false;
    if (parsed.username !== "" || parsed.password !== "") return false;
    // A path/query/fragment (PR review, 2026-10-06): this value is later concatenated with
    // ezyVet-API-relative paths, so a pasted address like ".../dashboard" or "...?x=1" would make
    // every outbound request resolve against the wrong base, not just store a cosmetically odd URL.
    if (parsed.pathname !== "/" || parsed.search !== "" || parsed.hash !== "") return false;
    const host = parsed.hostname.toLowerCase();
    return host === "ezyvet.com" || host.endsWith(".ezyvet.com");
}
